import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2, Tags, Plus, Trash2, Edit3, Search, Hash, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const CATEGORY_COLORS = {
  segment: 'bg-blue-50 text-blue-700 border-blue-200',
  event: 'bg-purple-50 text-purple-700 border-purple-200',
  behavior: 'bg-amber-50 text-amber-700 border-amber-200',
  content: 'bg-teal-50 text-teal-700 border-teal-200',
  custom: 'bg-slate-50 text-slate-700 border-slate-200',
};

const CATEGORY_LABELS = {
  segment: 'Segmento',
  event: 'Evento',
  behavior: 'Comportamento',
  content: 'Contenuto',
  custom: 'Personalizzato',
};

export default function TagManagerPage() {
  const [catalog, setCatalog] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [newTag, setNewTag] = useState({ name: '', category: 'custom', description: '' });
  const [creating, setCreating] = useState(false);
  const [editTag, setEditTag] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [catRes, statsRes] = await Promise.all([
        api.get('/tags/catalog'),
        api.get('/tags/stats'),
      ]);
      setCatalog(catRes.data);
      setStats(statsRes.data);
    } catch { toast.error('Errore caricamento tag'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCreate = async () => {
    if (!newTag.name.trim()) return toast.error('Nome tag obbligatorio');
    setCreating(true);
    try {
      await api.post('/tags/catalog', newTag);
      toast.success('Tag creato');
      setShowCreate(false);
      setNewTag({ name: '', category: 'custom', description: '' });
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore creazione');
    } finally { setCreating(false); }
  };

  const handleDelete = async (tagId) => {
    if (!window.confirm('Eliminare questo tag? Verra rimosso anche da tutti i contatti.')) return;
    try {
      await api.delete(`/tags/catalog/${tagId}`);
      toast.success('Tag eliminato');
      fetchAll();
    } catch { toast.error('Errore eliminazione'); }
  };

  const handleUpdate = async () => {
    if (!editTag) return;
    try {
      await api.put(`/tags/catalog/${editTag.id}`, {
        category: editTag.category,
        description: editTag.description,
      });
      toast.success('Tag aggiornato');
      setEditTag(null);
      fetchAll();
    } catch { toast.error('Errore aggiornamento'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-[#2E75B6]" /></div>;

  const tags = (catalog?.tags || []).filter(t => {
    if (search && !t.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterCat !== 'all' && t.category !== filterCat) return false;
    return true;
  });

  return (
    <div className="space-y-5" data-testid="tag-manager-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-[#1A3A5C]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Gestione Tag</h1>
          <p className="text-xs text-[#64748B] mt-0.5">Catalogo centralizzato dei tag, statistiche di utilizzo</p>
        </div>
        <Button size="sm" onClick={() => setShowCreate(true)} className="bg-[#1A3A5C] hover:bg-[#1A3A5C]/90 text-xs"
          data-testid="create-tag-btn">
          <Plus className="w-3 h-3 mr-1" />Nuovo tag
        </Button>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-[#E2E8F0]"><CardContent className="p-3 text-center">
          <p className="text-lg font-bold text-[#1A3A5C]">{catalog?.total_tags || 0}</p>
          <p className="text-[9px] text-[#94A3B8]">Tag totali</p>
        </CardContent></Card>
        <Card className="border-[#E2E8F0]"><CardContent className="p-3 text-center">
          <p className="text-lg font-bold text-[#2E75B6]">{stats?.total_tagged_contacts || 0}</p>
          <p className="text-[9px] text-[#94A3B8]">Contatti con tag</p>
        </CardContent></Card>
        <Card className="border-[#E2E8F0]"><CardContent className="p-3 text-center">
          <p className="text-lg font-bold text-emerald-600">{stats?.tag_coverage || 0}%</p>
          <p className="text-[9px] text-[#94A3B8]">Copertura</p>
        </CardContent></Card>
        <Card className="border-[#E2E8F0]"><CardContent className="p-3 text-center">
          <p className="text-lg font-bold text-amber-600">{(catalog?.tags || []).filter(t => !t.is_cataloged).length}</p>
          <p className="text-[9px] text-[#94A3B8]">Non catalogati</p>
        </CardContent></Card>
      </div>

      {/* Top Tags Chart */}
      {stats?.top_tags?.length > 0 && (
        <Card className="border-[#E2E8F0]" data-testid="top-tags-chart">
          <CardContent className="p-4">
            <h3 className="text-xs font-bold text-[#1A3A5C] uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <BarChart3 className="w-3.5 h-3.5" /> Tag piu utilizzati
            </h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={stats.top_tags.slice(0, 12)} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis type="number" tick={{ fontSize: 9, fill: '#94A3B8' }} allowDecimals={false} />
                <YAxis type="category" dataKey="tag" tick={{ fontSize: 9, fill: '#94A3B8' }} width={140} />
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, borderColor: '#E2E8F0' }} />
                <Bar dataKey="count" name="Contatti" fill="#2E75B6" radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex gap-3 items-center">
        <div className="relative flex-1 max-w-xs">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca tag..."
            className="pl-9 h-8 text-xs" data-testid="tag-search" />
        </div>
        <Select value={filterCat} onValueChange={setFilterCat}>
          <SelectTrigger className="w-40 h-8 text-xs" data-testid="tag-filter-cat"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le categorie</SelectItem>
            {Object.entries(CATEGORY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Tag List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {tags.map(t => (
          <Card key={t.id || t.name} className={`border-[#E2E8F0] transition-shadow hover:shadow-sm ${!t.is_cataloged ? 'border-dashed' : ''}`}
            data-testid={`tag-card-${t.name}`}>
            <CardContent className="p-3">
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  <Hash className="w-3.5 h-3.5 text-[#94A3B8]" />
                  <span className="text-xs font-semibold text-[#1A3A5C]">{t.name}</span>
                </div>
                <div className="flex items-center gap-1">
                  {t.is_cataloged && t.id && (
                    <>
                      <button onClick={() => setEditTag({ ...t })} className="text-[#94A3B8] hover:text-[#2E75B6]"
                        data-testid={`edit-tag-${t.name}`}>
                        <Edit3 className="w-3 h-3" />
                      </button>
                      <button onClick={() => handleDelete(t.id)} className="text-[#94A3B8] hover:text-red-500"
                        data-testid={`delete-tag-${t.name}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 mb-1">
                <Badge className={`text-[8px] ${CATEGORY_COLORS[t.category] || CATEGORY_COLORS.custom}`}>
                  {CATEGORY_LABELS[t.category] || t.category}
                </Badge>
                <span className="text-[10px] text-[#64748B]">{t.usage_count || 0} contatti</span>
                {!t.is_cataloged && <Badge className="text-[7px] bg-amber-50 text-amber-700">Non catalogato</Badge>}
              </div>
              {t.description && <p className="text-[10px] text-[#94A3B8]">{t.description}</p>}
            </CardContent>
          </Card>
        ))}
        {tags.length === 0 && (
          <div className="col-span-full text-center py-8">
            <Tags className="w-8 h-8 text-[#CBD5E1] mx-auto mb-2" />
            <p className="text-sm text-[#64748B]">Nessun tag trovato</p>
          </div>
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader><DialogTitle className="text-sm text-[#1A3A5C]">Nuovo Tag</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={newTag.name} onChange={e => setNewTag({ ...newTag, name: e.target.value })}
              placeholder="Nome del tag" className="text-sm" data-testid="new-tag-name" />
            <Select value={newTag.category} onValueChange={v => setNewTag({ ...newTag, category: v })}>
              <SelectTrigger className="text-sm" data-testid="new-tag-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(CATEGORY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input value={newTag.description} onChange={e => setNewTag({ ...newTag, description: e.target.value })}
              placeholder="Descrizione (opzionale)" className="text-sm" data-testid="new-tag-description" />
            <Button onClick={handleCreate} disabled={creating} className="w-full bg-[#1A3A5C] hover:bg-[#1A3A5C]/90 text-sm"
              data-testid="confirm-create-tag">
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
              Crea tag
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editTag} onOpenChange={() => setEditTag(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="text-sm text-[#1A3A5C]">Modifica Tag: {editTag?.name}</DialogTitle></DialogHeader>
          {editTag && (
            <div className="space-y-3">
              <Select value={editTag.category} onValueChange={v => setEditTag({ ...editTag, category: v })}>
                <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input value={editTag.description || ''} onChange={e => setEditTag({ ...editTag, description: e.target.value })}
                placeholder="Descrizione" className="text-sm" />
              <Button onClick={handleUpdate} className="w-full bg-[#1A3A5C] hover:bg-[#1A3A5C]/90 text-sm"
                data-testid="confirm-update-tag">
                Salva modifiche
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
